/**
 * School Class Creation Form Component
 * Reference implementation following UI Standards (see /UI_STANDARDS.md)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SchoolClassCreate } from '../../types/schoolClass';
import { Subject } from '../../types/subject';
import { GRADE_LEVELS } from '../../types/common';

const SchoolClassForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dropdown data
  const [schools, setSchools] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);

  const [formData, setFormData] = useState<SchoolClassCreate>({
    name: '',
    subject: '',
    grade_level: 0,
    school_year: '',
    description: '',
    school_id: 0,
    teacher_id: 0,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Load schools, teachers, and subjects on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [schoolsRes, teachersRes, subjectsRes] = await Promise.all([
          axios.get('/api/schools/'),
          axios.get('/api/teachers/'),
          axios.get('/api/subjects/?ordering=name&page_size=1000'),
        ]);
        setSchools(schoolsRes.data.results || []);
        setTeachers(teachersRes.data.results || []);
        setSubjects(subjectsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load schools, teachers, or subjects');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Class name is required';
    }

    if (!formData.subject || formData.subject.trim() === '') {
      errors.subject = 'Subject is required';
    }

    if (!formData.grade_level || formData.grade_level === 0) {
      errors.grade_level = 'Grade level is required';
    } else if (formData.grade_level < 8 || formData.grade_level > 12) {
      errors.grade_level = 'Grade level must be between 8 and 12';
    }

    if (!formData.school_year || formData.school_year.trim() === '') {
      errors.school_year = 'School year is required';
    }

    if (!formData.school_id || formData.school_id === 0) {
      errors.school_id = 'School ID is required';
    }

    if (!formData.teacher_id || formData.teacher_id === 0) {
      errors.teacher_id = 'Teacher ID is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        description: formData.description || null,
      };

      await axios.post('/api/school-classes/', payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/classes');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating class:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);

      // Check for various error formats
      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle DRF validation errors (field-specific)
        if (typeof errorData === 'object' && !errorData.detail) {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${messages}`;
            })
            .join('; ');
          setError(errorMessages || 'Validation error occurred');
        }
        // Handle detail errors
        else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            setError(errorData.detail);
          } else if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail
              .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
              .join(', ');
            setError(errorMessages);
          } else {
            setError('Failed to create class. Please check your input.');
          }
        } else {
          setError('Failed to create class. Please try again.');
        }
      } else {
        setError('Failed to create class. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/classes');
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/classes')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Classes
        </MuiLink>
        <Typography color="text.primary">New Class</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Create New Class
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new class
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Class created successfully! Redirecting to classes list...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Class Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                disabled={loading}
              />
              <FormControl fullWidth required error={!!validationErrors.subject}>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  label="Subject"
                  disabled={loading || loadingData}
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.name}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.subject && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.subject}
                  </Typography>
                )}
              </FormControl>
            </Stack>

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={2}
              helperText="Optional"
            />

            <TextField
              fullWidth
              required
              label="School Year"
              name="school_year"
              value={formData.school_year}
              onChange={handleChange}
              error={!!validationErrors.school_year}
              helperText={validationErrors.school_year || 'e.g., 2024, 2024/2025'}
              disabled={loading}
              placeholder="2024"
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth required error={!!validationErrors.grade_level}>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  name="grade_level"
                  value={formData.grade_level || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, grade_level: Number(e.target.value) }))}
                  label="Grade Level"
                  disabled={loading || loadingData}
                >
                  {GRADE_LEVELS.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.grade_level && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.grade_level}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth required error={!!validationErrors.school_id}>
                <InputLabel>School</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, school_id: Number(e.target.value) }))}
                  label="School"
                  disabled={loading || loadingData}
                >
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.school_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.school_id}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth required error={!!validationErrors.teacher_id}>
                <InputLabel>Teacher</InputLabel>
                <Select
                  name="teacher_id"
                  value={formData.teacher_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, teacher_id: Number(e.target.value) }))}
                  label="Teacher"
                  disabled={loading || loadingData}
                >
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.teacher_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.teacher_id}
                  </Typography>
                )}
              </FormControl>
            </Stack>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Class'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SchoolClassForm;
